"use client";

import React, { useState } from "react";
import { PhoneCall, Info as HelpCircle, Clock, ShieldCheck, MapPin, Wrench } from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
interface FaqItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}
const faqList: FaqItem[] = [{
  id: "item-1",
  question: "What areas around Morrow do you cover?",
  answer: "We provide complete plumbing coverage across Morrow, GA and adjacent Clayton County communities. This includes neighborhoods near Southlake Mall, Reynolds Nature Preserve, Clayton State University, and major corridors along Jonesboro Road and I-75.",
  category: "Coverage"
}, {
  id: "item-2",
  question: "How fast can an emergency plumber arrive in Morrow?",
  answer: "Our local dispatch units average a 30 to 60-minute arrival window for emergency calls in Morrow. We operate dedicated mobile response vans stocked with diagnostic equipment and common replacement parts to start repairs immediately upon arrival.",
  category: "Response Time"
}, {
  id: "item-3",
  question: "What plumbing services are available for Morrow residents?",
  answer: "We handle the full range of plumbing needs including emergency burst pipe repair, sewer line backups, tankless and traditional water heater repair and installation, main line clog clearance, drain cleaning, and fixture repairs.",
  category: "Services"
}, {
  id: "item-4",
  question: "Are you available 24/7 for emergency plumbing in Morrow?",
  answer: "Yes. Emergency plumbing issues do not wait for business hours. Our master plumbers are on standby 24 hours a day, 7 days a week, 365 days a year—including weekends and holidays with zero delay in dispatching.",
  category: "Availability"
}, {
  id: "item-5",
  question: "How do I request immediate plumbing service or an estimate?",
  answer: "For immediate emergencies, call our direct phone line right away for live priority dispatch. For non-urgent repairs, scheduled maintenance, or new installations, you can also submit a request through our online contact form.",
  category: "Requests"
}, {
  id: "item-6",
  question: "What are your exact service boundaries around Clayton County?",
  answer: "Our core service boundary includes Morrow, Jonesboro, Forest Park, Lake City, Riverdale, and Stockbridge. If you are situated on the edge of these city limits, call us—our team routinely assists nearby residential and commercial properties.",
  category: "Boundaries"
}, {
  id: "item-7",
  question: "How do you ensure service quality and reliability?",
  answer: "Every repair is completed by licensed and insured plumbing specialists who adhere to Georgia plumbing codes. We offer transparent upfront pricing, work guarantees, and clear explanations before any repair begins.",
  category: "Reliability"
}];
export default function MorrowFaq() {
  const [activeTab, setActiveTab] = useState<string>("all");
  const filteredFaqs = activeTab === "all" ? faqList : faqList.filter(item => item.category.toLowerCase() === activeTab.toLowerCase());
  return <section id="morrowservicefaq" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <Badge variant="outline" className="mb-3 px-3 py-1 font-medium bg-secondary text-secondary-foreground border-border">
            <HelpCircle className="w-3.5 h-3.5 mr-1 text-primary" />
            Morrow Service Questions
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
            Frequently Asked Questions in Morrow, GA
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground">
            Clear answers about our response times, service boundaries, 24/7 emergency dispatch, and dependable plumbing solutions in Morrow.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-2 mt-8">
            <Button variant={activeTab === "all" ? "default" : "outline"} size="sm" onClick={() => setActiveTab("all")} className="rounded-full">
              All Questions
            </Button>
            <Button variant={activeTab === "response time" ? "default" : "outline"} size="sm" onClick={() => setActiveTab("response time")} className="rounded-full">
              <Clock className="w-3.5 h-3.5 mr-1" />
              Response Times
            </Button>
            <Button variant={activeTab === "coverage" ? "default" : "outline"} size="sm" onClick={() => setActiveTab("coverage")} className="rounded-full">
              <MapPin className="w-3.5 h-3.5 mr-1" />
              Coverage & Boundaries
            </Button>
            <Button variant={activeTab === "services" ? "default" : "outline"} size="sm" onClick={() => setActiveTab("services")} className="rounded-full">
              <Wrench className="w-3.5 h-3.5 mr-1" />
              Services & Pricing
            </Button>
          </div>
        </div>

        <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-sm">
          <Accordion type="single" collapsible defaultValue="item-1" className="w-full space-y-2">
            {filteredFaqs.map((faq, i) => <div key={faq.id} data-index={i}>
                <AccordionItem value={faq.id} className="border border-border/80 rounded-lg px-4 py-1 data-[state=open]:bg-secondary/40 transition-colors">
                  <AccordionTrigger className="hover:no-underline py-4 text-base sm:text-lg font-semibold text-foreground">
                    <span className="flex items-center gap-3 text-left">
                      <span className="text-primary font-mono text-sm font-bold bg-primary/10 rounded-md px-2 py-0.5">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      {faq.question}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pl-10 pr-4 pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              </div>)}
          </Accordion>
        </div>

        <div className="mt-12 bg-secondary/60 rounded-2xl border border-border p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0 shadow-xs">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-foreground">Have a plumbing emergency in Morrow right now?</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Our local technicians are ready 24/7 with fast arrival times across Clayton County.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            <a href="tel:4708479076" className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto gap-2 shadow-sm font-semibold">
                <PhoneCall className="w-4 h-4" />
                Call 24/7 Dispatch
              </Button>
            </a>
            <Button variant="outline" size="lg" asChild className="w-full sm:w-auto">
              <Link to="/contact/">Request Online</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}